import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Quote } from 'lucide-react';

/**
 * CockpitProofCarousel — Real client cockpits as social proof
 * 4 anonymized cockpits rotating every 6s on /prsnl
 *
 * Each cockpit shows:
 *  - Restaurant name (anonymized) + city + offer followed
 *  - Mini cockpit (EBE / Food cost / Prime cost / charges breakdown)
 *  - Short before/after testimonial
 */

const COCKPITS = [
  {
    id: 'cockpit-1',
    name: 'CONCEPT BISTRONOMIQUE',
    city: 'Marrakech',
    offer: 'Refonte 90j',
    period: 'Avant → Après · 90 jours',
    ebe: 11200,
    ebeBefore: 4200,
    ebeDelta: '+167%',
    ca: 38400,
    foodCost: 27.8,
    foodCostBefore: 34.2,
    primeCost: 58.4,
    primeCostBefore: 67.5,
    quote: '« On était à 4 200€ d\'EBE. En 90 jours avec Sabril, on a passé les 11 000€. La structure était là, on ne la voyait juste pas. »',
    owner: 'Y. K.',
  },
  {
    id: 'cockpit-2',
    name: 'CONCEPT HEALTHY',
    city: 'Lyon',
    offer: 'Refonte 90j',
    period: 'Avant → Après · 90 jours',
    ebe: 8400,
    ebeBefore: 1800,
    ebeDelta: '+367%',
    ca: 32000,
    foodCost: 29.1,
    foodCostBefore: 38.5,
    primeCost: 61.2,
    primeCostBefore: 72.8,
    quote: '« Food cost à 38% — je versais 6 000€/mois aux fournisseurs sans le voir. Sabril a tout cassé en 3 semaines. »',
    owner: 'L. M.',
  },
  {
    id: 'cockpit-3',
    name: 'NÉO-BISTRO',
    city: 'Paris 11e',
    offer: 'Ouverture 3 mois',
    period: 'Mois 6 post-ouverture',
    ebe: 14800,
    ebeBefore: null,
    ebeDelta: '29,5% / CA',
    ca: 50200,
    foodCost: 26.5,
    foodCostBefore: null,
    primeCost: 55.8,
    primeCostBefore: null,
    quote: '« Ouverture sans angle mort. Sabril m\'a structuré marque, équipe, ops avant même qu\'on lève le rideau. Mois 6 → complet tous les soirs. »',
    owner: 'K. B.',
  },
  {
    id: 'cockpit-4',
    name: 'STREET FOOD',
    city: 'Marrakech',
    offer: 'Refonte + 1 Day',
    period: 'Avant → Après · 6 mois',
    ebe: 6700,
    ebeBefore: -1200,
    ebeDelta: 'EBE → positif',
    ca: 22500,
    foodCost: 31.2,
    foodCostBefore: 41.0,
    primeCost: 62.5,
    primeCostBefore: 78.3,
    quote: '« EBE négatif depuis 8 mois. Le 1 Day a posé le diag, la refonte a tout réparé. Aujourd\'hui je me paye un salaire. »',
    owner: 'A. R.',
  },
];

const Bar = ({ value, max = 100, color, threshold }) => {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/8">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="h-full rounded-full"
          style={{ background: color }}
        />
      </div>
      {threshold && <span className="text-[8px] text-green-400 font-bold flex-shrink-0">≤ {threshold}%</span>}
    </div>
  );
};

const CockpitCard = ({ c }) => (
  <div
    className="rounded-3xl p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-6 relative overflow-hidden"
    style={{
      background: 'linear-gradient(180deg, #1a0e0e 0%, #0a0505 100%)',
      border: '1px solid rgba(240,90,26,0.18)',
      boxShadow: '0 20px 80px rgba(0,0,0,0.4)',
    }}
  >
    {/* Halo */}
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{ background: 'radial-gradient(circle at 20% 20%, rgba(34,197,94,0.10) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(240,90,26,0.08) 0%, transparent 50%)' }}
    />

    {/* LEFT — Identity + Quote */}
    <div className="lg:col-span-2 relative flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-black tracking-[3px] text-orange-400 uppercase">Cas client réel</span>
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
      </div>
      <h3 className="font-bebas tracking-tight text-white text-3xl sm:text-4xl leading-none">{c.name}</h3>
      <p className="text-white/55 text-sm mt-1">{c.city} · {c.offer}</p>

      <div className="mt-5 flex-1 flex flex-col justify-end">
        <Quote className="w-7 h-7 text-orange-400/40 mb-2" strokeWidth={2} />
        <p className="text-white/85 text-sm sm:text-base italic leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
          {c.quote}
        </p>
        <p className="text-white/45 text-xs mt-3 font-bold uppercase tracking-widest">— {c.owner} · Propriétaire</p>
      </div>
    </div>

    {/* RIGHT — Cockpit metrics */}
    <div className="lg:col-span-3 relative">
      <p className="text-[9px] font-bold tracking-[3px] uppercase text-white/45 mb-2">📊 Cockpit · {c.period}</p>

      {/* EBE Hero */}
      <div
        className="rounded-2xl p-4 mb-3"
        style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(34,197,94,0.04) 100%)', border: '1px solid rgba(34,197,94,0.3)' }}
      >
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold tracking-widest text-green-400/80 uppercase mb-1">EBE — Marge nette</p>
            <p className="font-bebas text-5xl text-green-400 tabular-nums leading-none" style={{ textShadow: '0 0 30px rgba(34,197,94,0.35)' }}>
              +{c.ebe.toLocaleString('fr-FR')}€
            </p>
            {c.ebeBefore !== null && (
              <p className="text-white/45 text-xs mt-1">
                Avant : <span className="line-through">{c.ebeBefore.toLocaleString('fr-FR')}€</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.30)' }}>
              <TrendingUp className="w-3 h-3 text-green-400" strokeWidth={2.5} />
              <span className="text-green-400 text-xs font-black">{c.ebeDelta}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Food cost + Prime cost */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p className="text-[8px] font-bold tracking-widest text-orange-400/80 uppercase mb-1">Food cost</p>
          <p className="font-bebas text-2xl text-white tabular-nums leading-none">{c.foodCost}<span className="text-base text-white/40">%</span></p>
          <div className="mt-2"><Bar value={c.foodCost} max={50} color="#22C55E" threshold={30} /></div>
          {c.foodCostBefore !== null && (
            <p className="text-[9px] text-white/45 mt-1.5">
              Avant : <span className="text-red-400 line-through">{c.foodCostBefore}%</span> · <span className="text-green-400 font-bold">−{(c.foodCostBefore - c.foodCost).toFixed(1)} pts</span>
            </p>
          )}
        </div>
        <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p className="text-[8px] font-bold tracking-widest text-blue-400/80 uppercase mb-1">Prime cost</p>
          <p className="font-bebas text-2xl text-white tabular-nums leading-none">{c.primeCost}<span className="text-base text-white/40">%</span></p>
          <div className="mt-2"><Bar value={c.primeCost} max={100} color="#22C55E" threshold={65} /></div>
          {c.primeCostBefore !== null && (
            <p className="text-[9px] text-white/45 mt-1.5">
              Avant : <span className="text-red-400 line-through">{c.primeCostBefore}%</span> · <span className="text-green-400 font-bold">−{(c.primeCostBefore - c.primeCost).toFixed(1)} pts</span>
            </p>
          )}
        </div>
      </div>

      {/* CA */}
      <div className="rounded-xl p-2.5 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <span className="text-[10px] text-white/55 uppercase tracking-widest">CA mensuel</span>
        <span className="font-bebas text-xl text-white tabular-nums">{c.ca.toLocaleString('fr-FR')}€</span>
      </div>
    </div>
  </div>
);

const CockpitProofCarousel = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setIndex((i) => (i + 1) % COCKPITS.length), 7500);
    return () => clearInterval(interval);
  }, []);

  const current = COCKPITS[index];

  return (
    <section
      className="py-14 sm:py-20 px-5 sm:px-8 border-t"
      style={{ borderColor: 'rgba(255,255,255,0.06)', background: '#0a0505' }}
      data-testid="cockpit-proof-section"
    >
      <div className="max-w-[1100px] mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <p className="text-[10px] sm:text-xs font-black tracking-[4px] uppercase mb-3" style={{ color: '#F05A1A' }}>
            ⚡ Proof · Cockpits clients réels
          </p>
          <h2 className="font-bebas leading-[0.95] tracking-tight text-white" style={{ fontSize: 'clamp(36px, 6vw, 64px)' }}>
            VOICI COMMENT LES PATRONS<br />
            <span style={{ color: '#F05A1A' }}>QUI ONT DÉJÀ DIT OUI</span> PILOTENT.
          </h2>
          <p className="text-white/55 text-sm sm:text-base max-w-xl mx-auto mt-3">
            4 cockpits de vrais clients PRSNL · données anonymisées · chiffres bruts.
          </p>
        </div>

        {/* Carousel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`cockpit-card-${current.id}`}
          >
            <CockpitCard c={current} />
          </motion.div>
        </AnimatePresence>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6" data-testid="cockpit-dots">
          {COCKPITS.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setIndex(i)}
              data-testid={`cockpit-dot-${c.id}`}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === index ? 28 : 8,
                background: i === index ? '#F05A1A' : 'rgba(255,255,255,0.20)',
              }}
              aria-label={`Voir cockpit ${c.name}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CockpitProofCarousel;
